// ============================================================
// EventPlatform_Part2.jsx  –  Screens 6-10
// ============================================================
import React, { useState, useEffect } from "react";
import { Fetchexecutequery, Fetchheaderdata, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from '../../../services/process/process_data/process_data';
import {
  FileText, Bell, CheckCircle, Clock, Calendar, Users, MapPin,
  Search, Filter, Eye, Edit3, Trash2, Copy, BarChart2, Globe,
  Upload, Plus, X, AlertCircle, Check, Send, ArrowRight, Building,
  Award, Archive, Zap, List, Flag, ChevronRight, ChevronLeft,
  ChevronDown, ChevronUp, MoreVertical, Save, AlertTriangle,
  ThumbsUp, ThumbsDown, MessageSquare, RotateCcw, UserPlus,
  Activity, Target, TrendingUp, Settings, Download, RefreshCw,
} from "lucide-react";

// ── Re-export shared primitives (in a real project these come from a shared file)
const C = {
  primary: "#14253d", accent: "#2563eb", accent2: "#0f766e",
  warn: "#d97706", danger: "#dc2626", success: "#16a34a",
  purple: "#7c3aed", bg: "#eef3f8", card: "#ffffff",
  border: "#d7e0ea", text: "#0f172a", muted: "#64748b", light: "#e8eef5",
};

const AppShell = ({ children, style = {} }) => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      background:
        "radial-gradient(circle at top left, rgba(37,99,235,.08), transparent 30%), radial-gradient(circle at top right, rgba(15,118,110,.08), transparent 26%), linear-gradient(180deg, #f7fafc 0%, #eef4fa 100%)",
      color: C.text,
      fontFamily: "'Segoe UI', sans-serif",
      ...style,
    }}
  >
    {children}
  </div>
);

const PageWrap = ({ children, wide = false, style = {} }) => (
  <div
    style={{
      width: "100%",
      maxWidth: wide ? 1520 : 1320,
      margin: "0 auto",
      padding: "24px 28px 32px",
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── User credentials from sessionStorage (Production Mode) ──────────────────
const getUserCredentials = () => JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

/* MOCK CREDENTIALS - COMMENTED OUT FOR PRODUCTION
const MOCK_CREDENTIALS = {
  user_id: 101, user_name: "admin_user", tenants_id: 1,
  groups_id: 2, selectedentity: 10, role: "form_filler", display_name: "St. Xavier's School",
};
*/

const MOCK_APPLICATIONS = [
  { id: 1, event_name: "Global EdTech Summit 2025", organizer: "EduWorld Org", applied_on: "2025-08-12", application_status: "shortlisted", review_status: "completed", invitation_status: "not_invited", last_updated_on: "2025-08-20" },
  { id: 2, event_name: "STEM Innovation Expo", organizer: "TechLearn Asia", applied_on: "2025-08-18", application_status: "under_review", review_status: "in_review", invitation_status: "not_invited", last_updated_on: "2025-08-22" },
  { id: 3, event_name: "South Asia Education Fair", organizer: "EduFair India", applied_on: "2025-07-30", application_status: "invited", review_status: "completed", invitation_status: "invited", last_updated_on: "2025-08-25" },
  { id: 4, event_name: "Digital Learning Conference", organizer: "EdTech Alliance", applied_on: "2025-07-10", application_status: "rejected", review_status: "completed", invitation_status: "not_invited", last_updated_on: "2025-08-01" },
  { id: 5, event_name: "International School Leaders Forum", organizer: "Global Ed Leaders", applied_on: "2025-08-05", application_status: "applied", review_status: "pending", invitation_status: "not_invited", last_updated_on: "2025-08-05" },
];

const MOCK_INVITATIONS = [
  { id: 1, event_name: "South Asia Education Fair", organizer: "EduFair India", invitation_date: "2025-08-25", acceptance_deadline: "2025-09-10", event_date: "2025-10-30", invitation_status: "pending" },
  { id: 2, event_name: "Global EdTech Summit 2025", organizer: "EduWorld Org", invitation_date: "2025-08-28", acceptance_deadline: "2025-09-15", event_date: "2025-11-15", invitation_status: "pending" },
];

const statusColors = {
  applied: { bg: "#dbeafe", text: "#1d4ed8" }, under_review: { bg: "#fef3c7", text: "#92400e" },
  shortlisted: { bg: "#d1fae5", text: "#065f46" }, invited: { bg: "#ede9fe", text: "#5b21b6" },
  accepted: { bg: "#d1fae5", text: "#065f46" }, rejected: { bg: "#fee2e2", text: "#991b1b" },
  expired: { bg: "#f3f4f6", text: "#6b7280" }, draft: { bg: "#f3f4f6", text: "#6b7280" },
  published: { bg: "#dbeafe", text: "#1d4ed8" }, registration_open: { bg: "#d1fae5", text: "#065f46" },
  pending: { bg: "#fef3c7", text: "#92400e" }, not_invited: { bg: "#f3f4f6", text: "#6b7280" },
  completed: { bg: "#d1fae5", text: "#065f46" }, archived: { bg: "#f3f4f6", text: "#6b7280" },
  active: { bg: "#d1fae5", text: "#065f46" },
};

const Badge = ({ status, label }) => {
  const s = statusColors[status] || { bg: "#f3f4f6", text: "#6b7280" };
  return (
    <span style={{ background: s.bg, color: s.text, padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {label || status?.replace(/_/g, " ")}
    </span>
  );
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.card, borderRadius: 18, border: `1px solid rgba(148,163,184,.22)`, boxShadow: "0 16px 40px rgba(15,23,42,.06)", ...style }}>{children}</div>
);

const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
    <div>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.text, letterSpacing: -.2 }}>{title}</h2>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{sub}</p>}
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card style={{ padding: "20px 22px", flex: 1, minWidth: 160 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: C.text, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{ background: color + "14", borderRadius: 14, padding: 12 }}>
        <Icon size={24} color={color} />
      </div>
    </div>
  </Card>
);

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", border: "none", transition: "all .15s", opacity: disabled ? .6 : 1, ...style };
  const variants = {
    primary: { background: C.accent, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    secondary: { background: C.light, color: C.primary, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    danger: { background: C.danger, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    success: { background: C.success, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    ghost: { background: "transparent", color: C.accent, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    outline: { background: "transparent", color: C.primary, border: `1.5px solid ${C.border}`, padding: size === "sm" ? "5px 13px" : "9px 19px", fontSize: size === "sm" ? 12 : 14 },
    warn: { background: C.warn, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} />}
      {children}
    </button>
  );
};

const Input = ({ ...props }) => (
  <input {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }} />
);

const Select = ({ children, ...props }) => (
  <select {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }}>
    {children}
  </select>
);

const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const BarChart = ({ data, color = C.accent }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ width: "100%", height: `${(d.value / max) * 64}px`, background: color, borderRadius: "4px 4px 0 0", minHeight: 4 }} />
          <span style={{ fontSize: 9, color: C.muted, textAlign: "center" }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const Countdown = ({ deadline }) => {
  const [diff, setDiff] = useState(0);
  useEffect(() => {
    const update = () => setDiff(Math.max(0, new Date(deadline) - Date.now()));
    update(); const t = setInterval(update, 1000); return () => clearInterval(t);
  }, [deadline]);
  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff % 86400000) / 3600000);
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[["D", days], ["H", hrs]].map(([u, v]) => (
        <div key={u} style={{ background: C.primary, color: "#fff", borderRadius: 6, padding: "4px 8px", textAlign: "center", minWidth: 36 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{String(v).padStart(2, "0")}</div>
          <div style={{ fontSize: 9, opacity: .7 }}>{u}</div>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ icon: Icon = FileText, title, sub, action }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: 12, textAlign: "center" }}>
    <div style={{ background: C.light, borderRadius: 16, padding: 20 }}><Icon size={32} color={C.muted} /></div>
    <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</div>
    <div style={{ fontSize: 13, color: C.muted, maxWidth: 280 }}>{sub}</div>
    {action}
  </div>
);

// \u2500\u2500 Mock data - COMMENTED OUT FOR PRODUCTION \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n/*\nconst MOCK_APPLICATIONS = [...];\nconst MOCK_INVITATIONS = [...];\n\n// Using production services: Fetchexecutequery, Fetchheaderdata, Fetchupdatequery, CreateData\n*/ \nconst MOCK_APPLICATIONS = [];\nconst MOCK_INVITATIONS = [];

const FALLBACK_APPLICATIONS = [
  {
    id: 1,
    event_name: "Global EdTech Summit 2025",
    organizer: "EduWorld Org",
    applied_on: "2025-08-12",
    application_status: "shortlisted",
    review_status: "under_review",
    invitation_status: "pending",
    last_updated_on: "2025-08-18",
  },
  {
    id: 2,
    event_name: "STEM Innovation Expo",
    organizer: "Future Learning Network",
    applied_on: "2025-08-20",
    application_status: "applied",
    review_status: "pending",
    invitation_status: "not_invited",
    last_updated_on: "2025-08-20",
  },
];

const FALLBACK_INVITATIONS = [
  {
    id: 1,
    event_name: "Global EdTech Summit 2025",
    organizer: "EduWorld Org",
    event_date: "2025-11-15",
    invitation_date: "2025-08-25",
    acceptance_deadline: "2025-09-10",
    invitation_status: "pending",
  },
  {
    id: 2,
    event_name: "School Leadership Forum",
    organizer: "EduWorld Org",
    event_date: "2026-01-20",
    invitation_date: "2025-08-27",
    acceptance_deadline: "2025-09-15",
    invitation_status: "accepted",
  },
];

const CREATOR_EVENTS = [
  { id: 1, event_name: "Global EdTech Summit 2025", event_status: "registration_open", applications: 87, invitations: 0, confirmations: 0, event_date: "2025-11-15", creator_user_id: 101 },
  { id: 2, event_name: "STEM Innovation Expo", event_status: "draft", applications: 0, invitations: 0, confirmations: 0, event_date: "2025-12-05", creator_user_id: 101 },
  { id: 3, event_name: "School Leadership Forum", event_status: "invitations_released", applications: 142, invitations: 60, confirmations: 38, event_date: "2026-01-20", creator_user_id: 101 },
];

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 6 – MY APPLICATIONS PAGE
// ══════════════════════════════════════════════════════════════════════════
export function MyApplications() {
  const uc = getUserCredentials();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("applied_on");
  const applications = MOCK_APPLICATIONS.length ? MOCK_APPLICATIONS : FALLBACK_APPLICATIONS;

  // Query: SELECT a.*, e.event_name, e.city, e.country FROM event_platform.applications a
  // JOIN event_platform.events e ON a.event_id = e.id
  // WHERE a.applicant_user_id = ${uc.user_id} AND a.applicant_entity_id = ${uc.selectedentity} AND a.tenant_id = ${uc.tenants_id}

  const filtered = applications.filter(a => {
    return (
      (!search || a.event_name.toLowerCase().includes(search.toLowerCase())) &&
      (!statusFilter || a.application_status === statusFilter)
    );
  });

  const statusCounts = applications.reduce((acc, a) => { acc[a.application_status] = (acc[a.application_status] || 0) + 1; return acc; }, {});

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1400 }}>
        <SectionHeader
          title="My Applications"
          sub={`User #${uc.user_id} · Entity #${uc.selectedentity} · Tenant #${uc.tenants_id}`}
          action={<Btn variant="outline" size="sm" icon={Download}>Export</Btn>}
        />

        {/* Status summary pills */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <button onClick={() => setStatusFilter("")} style={{ background: !statusFilter ? C.primary : C.card, color: !statusFilter ? "#fff" : C.text, border: `1px solid ${C.border}`, borderRadius: 99, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            All ({applications.length})
          </button>
          {Object.entries(statusCounts).map(([s, cnt]) => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{ background: statusFilter === s ? C.primary : C.card, color: statusFilter === s ? "#fff" : C.text, border: `1px solid ${C.border}`, borderRadius: 99, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              {s.replace(/_/g, " ")} ({cnt})
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <Card style={{ padding: "12px 16px", marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by event name..." style={{ paddingLeft: 32 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 180 }}>
            <option value="">All Statuses</option>
            {["applied","under_review","shortlisted","invited","accepted","rejected","expired"].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </Select>
          <Select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width: 160 }}>
            <option value="applied_on">Sort: Applied Date</option>
            <option value="last_updated_on">Sort: Last Updated</option>
            <option value="event_name">Sort: Event Name</option>
          </Select>
        </Card>

        {/* Table */}
        <Card style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.light }}>
                  {["Event Name", "Organizer", "Applied On", "App Status", "Review Status", "Invitation Status", "Last Updated", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: .5, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : C.bg + "88" }}>
                    <td style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: C.text }}>{a.event_name}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted }}>{a.organizer}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>{a.applied_on}</td>
                    <td style={{ padding: "14px 16px" }}><Badge status={a.application_status} /></td>
                    <td style={{ padding: "14px 16px" }}><Badge status={a.review_status} /></td>
                    <td style={{ padding: "14px 16px" }}><Badge status={a.invitation_status} /></td>
                    <td style={{ padding: "14px 16px", fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{a.last_updated_on}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <Btn variant="ghost" size="sm" icon={Eye}>View</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <EmptyState icon={FileText} title="No applications found" sub="Try adjusting your search or filters" />}
          </div>
          {/* Pagination */}
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: C.muted }}>Showing {filtered.length} of {applications.length} applications</span>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="outline" size="sm" icon={ChevronLeft}>Prev</Btn>
              <div style={{ background: C.accent, color: "#fff", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700 }}>1</div>
              <Btn variant="outline" size="sm">2</Btn>
              <Btn variant="outline" size="sm" icon={ChevronRight}>Next</Btn>
            </div>
          </div>
        </Card>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 7 – INVITATIONS PAGE
// ══════════════════════════════════════════════════════════════════════════
export function InvitationsPage() {
  const uc = getUserCredentials();
  const [invitations, setInvitations] = useState(MOCK_INVITATIONS.length ? MOCK_INVITATIONS : FALLBACK_INVITATIONS);
  const [actionLoading, setActionLoading] = useState(null);

  // Query: SELECT i.*, e.event_name, e.city, e.country, e.event_date FROM event_platform.invitations i
  // JOIN event_platform.events e ON i.event_id = e.id
  // WHERE i.applicant_user_id = ${uc.user_id} AND i.tenant_id = ${uc.tenants_id}

  const handleAction = (id, action) => {
    setActionLoading({ id, action });
    // UPDATE event_platform.invitations SET invitation_status = '${action}', responded_on = NOW(), modified_by = ${uc.user_id} WHERE id = ${id} AND applicant_user_id = ${uc.user_id}
    setTimeout(() => {
      setInvitations(p => p.map(inv => inv.id === id ? { ...inv, invitation_status: action === "accept" ? "accepted" : "rejected" } : inv));
      setActionLoading(null);
    }, 800);
  };

  const pending = invitations.filter(i => i.invitation_status === "pending");
  const responded = invitations.filter(i => i.invitation_status !== "pending");

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1080 }}>
        <SectionHeader
          title="My Invitations"
          sub={`User #${uc.user_id} · Entity #${uc.selectedentity}`}
        />

        {pending.length > 0 && (
          <div style={{ background: "#fff7ed", border: `1.5px solid ${C.warn}`, borderRadius: 10, padding: "12px 16px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
            <Bell size={16} color={C.warn} />
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>You have {pending.length} invitation{pending.length > 1 ? "s" : ""} awaiting your response</span>
          </div>
        )}

        <SectionHeader title="Pending Invitations" sub={`${pending.length} require your response`} />
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 }}>
          {pending.map(inv => (
            <Card key={inv.id} style={{ padding: 0, overflow: "hidden", border: `1.5px solid ${C.accent}30` }}>
              <div style={{ display: "flex", gap: 0 }}>
                {/* Left accent */}
                <div style={{ width: 6, background: `linear-gradient(180deg, ${C.accent}, ${C.purple})`, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text }}>{inv.event_name}</h3>
                        <Badge status={inv.invitation_status} />
                      </div>
                      <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>by {inv.organizer}</div>
                      <div style={{ display: "flex", gap: 20, fontSize: 12, color: C.muted, flexWrap: "wrap" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Bell size={11} />Invited: {inv.invitation_date}</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={11} />Event: {inv.event_date}</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 4, color: C.danger }}><AlertCircle size={11} />Deadline: {inv.acceptance_deadline}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, textAlign: "center", marginBottom: 4, fontWeight: 600 }}>RESPONSE DEADLINE</div>
                        <Countdown deadline={inv.acceptance_deadline} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <Btn variant="success" size="sm" icon={Check} onClick={() => handleAction(inv.id, "accept")} disabled={!!actionLoading}>
                      {actionLoading?.id === inv.id && actionLoading?.action === "accept" ? "Accepting..." : "Accept Invitation"}
                    </Btn>
                    <Btn variant="danger" size="sm" icon={X} onClick={() => handleAction(inv.id, "reject")} disabled={!!actionLoading}>
                      {actionLoading?.id === inv.id && actionLoading?.action === "reject" ? "Declining..." : "Decline"}
                    </Btn>
                    <Btn variant="outline" size="sm" icon={Eye}>View Event</Btn>
                  </div>
                </div>
              </div>
            </Card>
          ))}
          {pending.length === 0 && <EmptyState icon={Bell} title="No pending invitations" sub="You're all caught up! New invitations will appear here." />}
        </div>

        {responded.length > 0 && (
          <>
            <SectionHeader title="Past Invitations" />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {responded.map(inv => (
                <Card key={inv.id} style={{ padding: 16, display: "flex", alignItems: "center", gap: 16, opacity: .8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{inv.event_name}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{inv.organizer} · Event: {inv.event_date}</div>
                  </div>
                  <Badge status={inv.invitation_status} />
                  <Btn variant="ghost" size="sm" icon={Eye}>View</Btn>
                </Card>
              ))}
            </div>
          </>
        )}
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 8 – EVENT CREATOR DASHBOARD
// ══════════════════════════════════════════════════════════════════════════
export function EventCreatorDashboard() {
  const uc = { ...getUserCredentials(), role: "event_creator", display_name: "EduWorld Org" };

  // NOTE: All queries filter by creator_user_id = ${uc.user_id} AND creator_entity_id = ${uc.selectedentity}
  // This ensures no cross-contamination between creators

  const monthData = [
    { label: "Mar", value: 14 }, { label: "Apr", value: 22 }, { label: "May", value: 18 },
    { label: "Jun", value: 35 }, { label: "Jul", value: 28 }, { label: "Aug", value: 87 },
  ];

  const funnelData = [
    { label: "Total Applicants", value: 87, color: C.accent },
    { label: "Shortlisted", value: 60, color: C.purple },
    { label: "Invited", value: 45, color: C.warn },
    { label: "Accepted", value: 38, color: C.success },
  ];

  const upcomingDeadlines = [
    { event: "Global EdTech Summit", deadline: "2025-10-01", type: "Registration Close", urgent: true },
    { event: "STEM Innovation Expo", deadline: "2025-11-10", type: "Registration Close", urgent: false },
    { event: "School Leaders Forum", deadline: "2025-09-10", type: "Acceptance Deadline", urgent: true },
  ];

  return (
    <AppShell>
      <PageWrap wide>
        <SectionHeader
          title={`Creator Dashboard — ${uc.display_name}`}
          sub={`Showing your events only · User #${uc.user_id} · Entity #${uc.selectedentity}`}
          action={<Btn variant="outline" size="sm" icon={Plus}>Create Event</Btn>}
        />

        {/* Stats */}
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard label="Total Events" value="3" icon={Calendar} color={C.accent} sub="All time" />
          <StatCard label="Active Events" value="2" icon={Zap} color={C.success} sub="Currently open" />
          <StatCard label="Applications" value="229" icon={FileText} color={C.purple} sub="Received this month" />
          <StatCard label="Invitations Sent" value="105" icon={Send} color={C.warn} sub="Across all events" />
          <StatCard label="Accepted" value="76" icon={CheckCircle} color={C.accent2} sub="Confirmed" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 20 }}>
          {/* Applications trend */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="Applications Trend" sub="Last 6 months" />
            <BarChart data={monthData} color={C.accent} />
          </Card>

          {/* Upcoming deadlines */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="Upcoming Deadlines" />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {upcomingDeadlines.map((d, i) => (
                <div key={i} style={{ padding: "12px 14px", background: d.urgent ? "#fff7ed" : C.bg, border: `1px solid ${d.urgent ? C.warn : C.border}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 12, color: C.text }}>{d.event}</div>
                      <div style={{ fontSize: 11, color: d.urgent ? C.warn : C.muted, marginTop: 2 }}>{d.type}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: d.urgent ? C.danger : C.muted }}>{d.deadline}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Conversion funnel */}
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <SectionHeader title="Conversion Funnel" sub="Global EdTech Summit 2025" />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            {funnelData.map((f, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 18, color: f.color }}>{f.value}</div>
                <div style={{ width: `${(4 - i) * 25}%`, height: 10, background: f.color, borderRadius: 4 }} />
                <div style={{ fontSize: 11, color: C.muted, textAlign: "center" }}>{f.label}</div>
                {i < funnelData.length - 1 && (
                  <div style={{ fontSize: 10, color: C.muted }}>
                    ↓ {Math.round((funnelData[i + 1].value / f.value) * 100)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Recent applications */}
        <Card style={{ padding: 24 }}>
          <SectionHeader title="Recent Applications" sub="Across your events" action={<Btn variant="ghost" size="sm" icon={ArrowRight}>View All</Btn>} />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.light }}>
                {["School", "Country", "Event", "Applied", "Status"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { school: "St. Xavier's High School", country: "India", event: "Global EdTech Summit", applied: "2025-08-12", status: "shortlisted" },
                { school: "GreenLeaf International", country: "UAE", event: "Global EdTech Summit", applied: "2025-08-14", status: "under_review" },
                { school: "Cambridge Prep School", country: "UK", event: "Global EdTech Summit", applied: "2025-08-18", status: "applied" },
              ].map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: C.text }}>{r.school}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: C.muted }}>{r.country}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: C.muted }}>{r.event}</td>
                  <td style={{ padding: "12px 14px", fontSize: 12, color: C.muted }}>{r.applied}</td>
                  <td style={{ padding: "12px 14px" }}><Badge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 9 – CREATE EVENT (Multi-step wizard)
// ══════════════════════════════════════════════════════════════════════════
export function CreateEvent() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    event_name: "", event_category: "", description: "", industry: "",
    country: "", city: "", venue: "",
    reg_open_date: "", reg_open_time: "", reg_close_date: "", reg_close_time: "",
    review_start_date: "", review_end_date: "",
    invitation_release_date: "", acceptance_deadline: "", event_date: "",
    max_applicants: "", max_invitations: "", eligibility_criteria: "",
  });

  const totalSteps = 6;
  const steps = [
    "Basic Info", "Location", "Registration", "Review Setup", "Invitation Setup", "Participation Rules"
  ];

  const handle = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  const handleSave = (status) => {
    setSaving(true);
    // INSERT INTO event_platform.events (...formData, creator_user_id=uc.user_id, creator_entity_id=uc.selectedentity, tenant_id=uc.tenants_id, event_status=status, created_by=uc.user_id, created_on=NOW())
    setTimeout(() => setSaving(false), 800);
  };

  const stepContent = {
    1: (
      <div>
        <Field label="Event Name" required>
          <Input value={formData.event_name} onChange={e => handle("event_name", e.target.value)} placeholder="e.g. Global EdTech Summit 2025" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Event Category" required>
            <Select value={formData.event_category} onChange={e => handle("event_category", e.target.value)}>
              <option value="">Select category</option>
              {["Conference","Summit","Expo","Fair","Workshop","Forum","Competition"].map(c => <option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Industry">
            <Select value={formData.industry} onChange={e => handle("industry", e.target.value)}>
              <option value="">Select industry</option>
              {["Education","Technology","Science","Arts","Leadership","Innovation"].map(c => <option key={c}>{c}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Description" required>
          <textarea value={formData.description} onChange={e => handle("description", e.target.value)} placeholder="Describe your event..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, minHeight: 120, resize: "vertical", boxSizing: "border-box" }} />
        </Field>
        <Field label="Event Banner">
          <div style={{ border: `2px dashed ${C.border}`, borderRadius: 10, padding: 28, textAlign: "center", cursor: "pointer", background: C.bg }}>
            <Upload size={24} color={C.muted} style={{ margin: "0 auto 8px" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Click to upload or drag and drop</div>
            <div style={{ fontSize: 11, color: C.muted }}>PNG, JPG up to 5MB · Recommended: 1200×400px</div>
          </div>
        </Field>
        <Field label="Event Date" required>
          <Input type="date" value={formData.event_date} onChange={e => handle("event_date", e.target.value)} />
        </Field>
      </div>
    ),
    2: (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Country" required>
            <Select value={formData.country} onChange={e => handle("country", e.target.value)}>
              <option value="">Select country</option>
              {["India","UAE","Singapore","UK","USA","Australia","Canada"].map(c => <option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="City" required>
            <Input value={formData.city} onChange={e => handle("city", e.target.value)} placeholder="e.g. Dubai" />
          </Field>
        </div>
        <Field label="Venue / Address" required>
          <textarea value={formData.venue} onChange={e => handle("venue", e.target.value)} placeholder="Full venue name and address" style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, minHeight: 80, resize: "vertical", boxSizing: "border-box" }} />
        </Field>
      </div>
    ),
    3: (
      <div>
        <div style={{ marginBottom: 20, padding: 14, background: C.light, borderRadius: 8, fontSize: 13, color: C.muted }}>
          <AlertCircle size={14} style={{ marginRight: 6 }} />Set the window during which schools can submit applications.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Registration Open Date" required><Input type="date" value={formData.reg_open_date} onChange={e => handle("reg_open_date", e.target.value)} /></Field>
          <Field label="Registration Open Time" required><Input type="time" value={formData.reg_open_time} onChange={e => handle("reg_open_time", e.target.value)} /></Field>
          <Field label="Registration Close Date" required><Input type="date" value={formData.reg_close_date} onChange={e => handle("reg_close_date", e.target.value)} /></Field>
          <Field label="Registration Close Time" required><Input type="time" value={formData.reg_close_time} onChange={e => handle("reg_close_time", e.target.value)} /></Field>
        </div>
      </div>
    ),
    4: (
      <div>
        <div style={{ marginBottom: 20, padding: 14, background: C.light, borderRadius: 8, fontSize: 13, color: C.muted }}>
          <AlertCircle size={14} style={{ marginRight: 6 }} />The review period is when you evaluate applicant profiles.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Review Start Date" required><Input type="date" value={formData.review_start_date} onChange={e => handle("review_start_date", e.target.value)} /></Field>
          <Field label="Review End Date" required><Input type="date" value={formData.review_end_date} onChange={e => handle("review_end_date", e.target.value)} /></Field>
        </div>
      </div>
    ),
    5: (
      <div>
        <div style={{ marginBottom: 20, padding: 14, background: C.light, borderRadius: 8, fontSize: 13, color: C.muted }}>
          <AlertCircle size={14} style={{ marginRight: 6 }} />Accepted schools must confirm within the acceptance deadline.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Invitation Release Date" required><Input type="date" value={formData.invitation_release_date} onChange={e => handle("invitation_release_date", e.target.value)} /></Field>
          <Field label="Acceptance Deadline" required><Input type="date" value={formData.acceptance_deadline} onChange={e => handle("acceptance_deadline", e.target.value)} /></Field>
        </div>
      </div>
    ),
    6: (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Max Applicants" hint="0 = unlimited">
            <Input type="number" value={formData.max_applicants} onChange={e => handle("max_applicants", e.target.value)} placeholder="e.g. 500" />
          </Field>
          <Field label="Max Invitations" hint="How many you'll invite">
            <Input type="number" value={formData.max_invitations} onChange={e => handle("max_invitations", e.target.value)} placeholder="e.g. 120" />
          </Field>
        </div>
        <Field label="Eligibility Criteria">
          <textarea value={formData.eligibility_criteria} onChange={e => handle("eligibility_criteria", e.target.value)} placeholder="Describe who can apply..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, minHeight: 120, resize: "vertical", boxSizing: "border-box" }} />
        </Field>
      </div>
    ),
  };

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1080 }}>
        <SectionHeader
          title="Create New Event"
          sub={`Creator: User #${uc.user_id} · Entity #${uc.selectedentity}`}
          action={<Btn variant="outline" size="sm" icon={Save} onClick={() => handleSave("draft")}>Save Draft</Btn>}
        />

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 0, marginBottom: 32, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          {steps.map((s, i) => (
            <div key={i} onClick={() => setStep(i + 1)} style={{
              flex: 1, padding: "12px 8px", textAlign: "center", cursor: "pointer",
              background: step === i + 1 ? C.accent : i + 1 < step ? C.success : "transparent",
              color: step === i + 1 || i + 1 < step ? "#fff" : C.muted,
              transition: "all .2s", borderRight: i < steps.length - 1 ? `1px solid ${C.border}` : "none",
            }}>
              <div style={{ fontSize: 11, fontWeight: 800 }}>Step {i + 1}</div>
              <div style={{ fontSize: 11, opacity: .85, marginTop: 2 }}>{s}</div>
              {i + 1 < step && <Check size={10} style={{ marginTop: 2 }} />}
            </div>
          ))}
        </div>

        <Card style={{ padding: 32 }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: C.text }}>Step {step}: {steps[step - 1]}</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted }}>Step {step} of {totalSteps}</p>
          </div>

          {stepContent[step]}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
            <Btn variant="outline" icon={ChevronLeft} onClick={() => setStep(p => Math.max(1, p - 1))} disabled={step === 1}>Previous</Btn>
            <div style={{ display: "flex", gap: 12 }}>
              <Btn variant="outline" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>Save Draft</Btn>
              {step < totalSteps ? (
                <Btn variant="primary" onClick={() => setStep(p => Math.min(totalSteps, p + 1))}>
                  Next <ChevronRight size={14} />
                </Btn>
              ) : (
                <Btn variant="success" icon={Zap} onClick={() => handleSave("published")} disabled={saving}>
                  {saving ? "Publishing..." : "Publish Event"}
                </Btn>
              )}
            </div>
          </div>
        </Card>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 10 – EVENT MANAGEMENT PAGE
// ══════════════════════════════════════════════════════════════════════════
export function EventManagement() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const events = CREATOR_EVENTS;

  // SELECT * FROM event_platform.events
  // WHERE creator_user_id = ${uc.user_id} AND creator_entity_id = ${uc.selectedentity} AND tenant_id = ${uc.tenants_id} AND is_active = 1

  const filtered = events.filter(ev => {
    return (
      (!search || ev.event_name.toLowerCase().includes(search.toLowerCase())) &&
      (!statusFilter || ev.event_status === statusFilter)
    );
  });

  const ActionMenu = ({ ev }) => {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ position: "relative" }}>
        <button onClick={() => setOpen(p => !p)} style={{ background: C.light, border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer" }}>
          <MoreVertical size={14} color={C.muted} />
        </button>
        {open && (
          <div style={{ position: "absolute", right: 0, top: "100%", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 100, minWidth: 160, padding: 6 }}>
            {[
              { label: "Edit Event", icon: Edit3 }, { label: "View Applicants", icon: Users },
              { label: "View Analytics", icon: BarChart2 }, { label: "Clone Event", icon: Copy },
              { label: "Archive", icon: Archive, danger: true },
            ].map(item => (
              <button key={item.label} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", borderRadius: 6, color: item.danger ? C.danger : C.text, fontSize: 13, fontWeight: 600, textAlign: "left" }}>
                <item.icon size={13} /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1400 }}>
        <SectionHeader
          title="My Events"
          sub={`Creator: User #${uc.user_id} · Entity #${uc.selectedentity} — Isolated view`}
          action={<Btn variant="outline" size="sm" icon={Plus}>Create New Event</Btn>}
        />

        {/* Quick stats */}
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Events" value={CREATOR_EVENTS.length} icon={Calendar} color={C.accent} />
          <StatCard label="Active" value={CREATOR_EVENTS.filter(e => e.event_status === "registration_open").length} icon={Zap} color={C.success} />
          <StatCard label="Total Applications" value={CREATOR_EVENTS.reduce((a, e) => a + e.applications, 0)} icon={FileText} color={C.purple} />
          <StatCard label="Confirmed" value={CREATOR_EVENTS.reduce((a, e) => a + e.confirmations, 0)} icon={CheckCircle} color={C.accent2} />
        </div>

        {/* Filter */}
        <Card style={{ padding: "12px 16px", marginBottom: 20, display: "flex", gap: 12 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events..." style={{ paddingLeft: 32 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 200 }}>
            <option value="">All Statuses</option>
            {["draft","published","registration_open","invitations_released","completed"].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </Select>
        </Card>

        {/* Table */}
        <Card style={{ overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.light }}>
                {["Event Name", "Status", "Event Date", "Applications", "Invitations", "Confirmations", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev, i) => (
                <tr key={ev.id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : C.bg + "88" }}>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{ev.event_name}</div>
                  </td>
                  <td style={{ padding: "14px 16px" }}><Badge status={ev.event_status} /></td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted }}>{ev.event_date}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 16, color: C.accent }}>{ev.applications}</span>
                      <Btn variant="ghost" size="sm" icon={Eye} style={{ padding: "2px 8px" }}></Btn>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontWeight: 800, fontSize: 16, color: C.purple }}>{ev.invitations}</td>
                  <td style={{ padding: "14px 16px", fontWeight: 800, fontSize: 16, color: C.success }}>{ev.confirmations}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn variant="outline" size="sm" icon={Eye}>Review</Btn>
                      <ActionMenu ev={ev} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState icon={Calendar} title="No events found" sub="Create your first event to get started" action={<Btn variant="primary" icon={Plus}>Create Event</Btn>} />}
        </Card>
      </PageWrap>
    </AppShell>
  );
}
